import { debounceCall } from '@/utils';
import withSiteIdUpdater from 'HOCs/withSiteIdUpdater';
import { observer } from 'mobx-react-lite';
import React, { Suspense, lazy } from 'react';

import { GLOBAL_HAS_NO_RECORDINGS } from 'App/constants/storageKeys';
import { OB_DEFAULT_TAB } from 'App/routes';
import {
  Navigate,
  Route,
  StableRoutes,
  useHistory,
  useLocation,
} from 'App/routing';
import { Loader } from 'UI';

import { apiClient } from './api_client';
import { useStore } from './mstore';
import * as routes from './routes';
import { saasRoutes } from './saasComponents';

const components: any = {
  SessionPure: lazy(() => import('Components/Session/Session')),
  LiveSessionPure: lazy(() => import('Components/Session/LiveSession')),
  OnboardingPure: lazy(() => import('Components/Onboarding/Onboarding')),
  ClientPure: lazy(() => import('Components/Client/Client')),
  AssistPure: lazy(() => import('Components/Assist/AssistRouter')),
  SessionsOverviewPure: lazy(() => import('Components/Overview')),
  DashboardPure: lazy(() => import('Components/Dashboard/NewDashboard')),
  MultiviewPure: lazy(() => import('Components/Session_/Multiview/Multiview')),
  SpotsListPure: lazy(() => import('Components/Spots/SpotsList')),
  SpotPure: lazy(() => import('Components/Spots/SpotPlayer')),
  HighlightsPure: lazy(() => import('Components/Highlights/HighlightsList')),
  ActivityPure: lazy(
    () => import('Components/DataManagement/Activity/ActivityPage'),
  ),
  UserPage: lazy(
    () => import('Components/DataManagement/UsersEvents/UserPage'),
  ),
  UsersPage: lazy(
    () => import('Components/DataManagement/UsersEvents/UsersListPage'),
  ),
  EventsPage: lazy(() => import('Components/DataManagement/Events/index')),
  PropertiesList: lazy(
    () => import('Components/DataManagement/Properties/ListPage'),
  ),
  SegmentsPage: lazy(() => import('Components/DataManagement/Segments/index')),
  SegmentPage: lazy(
    () => import('Components/DataManagement/Segments/SegmentPage'),
  ),
  TagsPage: lazy(() => import('Components/DataManagement/Tags/index')),
  McpAuthorize: lazy(() => import('Components/McpAuthorize/McpAuthorize')),
};

const enhancedComponents: any = {
  SessionsOverview: withSiteIdUpdater(components.SessionsOverviewPure),
  Dashboard: withSiteIdUpdater(components.DashboardPure),
  Session: withSiteIdUpdater(components.SessionPure),
  LiveSession: withSiteIdUpdater(components.LiveSessionPure),
  Assist: withSiteIdUpdater(components.AssistPure),
  Client: withSiteIdUpdater(components.ClientPure),
  Onboarding: withSiteIdUpdater(components.OnboardingPure),
  Multiview: withSiteIdUpdater(components.MultiviewPure),
  SpotsList: withSiteIdUpdater(components.SpotsListPure),
  Spot: components.SpotPure,
  Highlights: withSiteIdUpdater(components.HighlightsPure),
  ScopeSetup: components.ScopeSetup,
  Activity: withSiteIdUpdater(components.ActivityPure),
  UserPage: withSiteIdUpdater(components.UserPage),
  UsersPage: withSiteIdUpdater(components.UsersPage),
  EventsPage: withSiteIdUpdater(components.EventsPage),
  PropertiesList: withSiteIdUpdater(components.PropertiesList),
  SegmentsPage: withSiteIdUpdater(components.SegmentsPage),
  SegmentPage: withSiteIdUpdater(components.SegmentPage),
  TagsPage: withSiteIdUpdater(components.TagsPage),
};

const { withSiteId } = routes;

const METRICS_PATH = routes.metrics();
const METRICS_DETAILS = routes.metricDetails();
const METRICS_DETAILS_SUB = routes.metricDetailsSub();

const ALERTS_PATH = routes.alerts();
const ALERT_CREATE_PATH = routes.alertCreate();
const ALERT_EDIT_PATH = routes.alertEdit();

const DASHBOARD_PATH = routes.dashboard();
const DASHBOARD_SELECT_PATH = routes.dashboardSelected();
const DASHBOARD_METRIC_CREATE_PATH = routes.dashboardMetricCreate();
const DASHBOARD_METRIC_DETAILS_PATH = routes.dashboardMetricDetails();

const SESSIONS_PATH = routes.sessions();
const NOTES_PATH = routes.notes();
const BOOKMARKS_PATH = routes.bookmarks();
const RECORDINGS_PATH = routes.recordings();
const SESSION_PATH = routes.session();
const CLIENT_PATH = routes.client();
const ONBOARDING_PATH = routes.onboarding();
const ONBOARDING_REDIRECT_PATH = routes.onboarding(OB_DEFAULT_TAB);

const ASSIST_PATH = routes.assist();
const LIVE_SESSION_PATH = routes.liveSession();
const MULTIVIEW_PATH = routes.multiview();
const MULTIVIEW_INDEX_PATH = routes.multiviewIndex();

const SPOTS_LIST_PATH = routes.spotsList();
const SPOT_PATH = routes.spot();

const HIGHLIGHTS_PATH = routes.highlights();

const SAAS_ROUTES = saasRoutes.map((route) => ({
  path: route.path,
  enabled: route.enabled,
  Component: withSiteIdUpdater(route.component),
}));

function PrivateRoutes() {
  const { projectsStore, userStore, searchStore, filterStore } = useStore();
  const location = useLocation();
  const history = useHistory();
  const onboarding = userStore.onboarding;
  const { tenantId } = userStore.account;
  const sites = projectsStore.list;
  const { siteId } = projectsStore;
  const hasRecordings = sites.some((s) => s.recorded);
  const redirectToOnboarding =
    !onboarding &&
    (localStorage.getItem(GLOBAL_HAS_NO_RECORDINGS) === 'true' ||
      (sites.length > 0 && !hasRecordings));
  const siteIdList: any = sites.map(({ id }) => id);

  const initialFetchDoneRef = React.useRef(false);
  const lastFiltersSiteIdRef = React.useRef<string | null>(null);
  const [filtersLoaded, setFiltersLoaded] = React.useState(false);

  const IntegrationsRedirect: React.FC = () => {
    const location = useLocation();

    React.useEffect(() => {
      switch (location.pathname) {
        case '/integrations/slack':
          apiClient.post('integrations/slack/add', {
            code: location.search.split('=')[1],
            state: tenantId,
          });
          break;
        case '/integrations/msteams':
          apiClient.post('integrations/msteams/add', {
            code: location.search.split('=')[1],
            state: tenantId,
          });
          break;
      }
    }, [location.pathname, location.search]);

    return <Navigate to={CLIENT_PATH} replace />;
  };

  React.useEffect(() => {
    if (searchStore.urlParsed || !siteId) return;

    const searchParams = new URLSearchParams(location.search);
    const searchId = searchParams.get('sid');
    const supportedFilters = [
      'userId',
      'distinct_id',
      'userCountry',
      'userCity',
      'meta_',
    ];
    const searchParamsKeys = Array.from(searchParams.keys());
    const parsedFilters = searchParamsKeys.filter((key) =>
      supportedFilters.some((f) =>
        f === 'meta_' ? key.startsWith('meta_') : key === f,
      ),
    );

    // only the two URL-driven branches read the filter catalogue; a plain load
    // builds its search body from searchStore alone, so it must not queue
    // behind /filters
    if ((searchId || parsedFilters.length > 0) && !filtersLoaded) return;

    if (searchId) {
      searchStore
        .loadSharedSearch(searchId)
        .then(() => {
          searchParams.delete('sid');
          const newSearch = searchParams.toString();
          const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
          history.replace(newUrl);
          searchStore.setUrlParsed();
          setTimeout(() => {
            initialFetchDoneRef.current = true;
          }, 500);
        })
        .catch((error) => {
          console.error('Failed to load shared search:', error);
          searchStore.setUrlParsed();
          setTimeout(() => {
            initialFetchDoneRef.current = true;
          }, 500);
        });
    } else if (parsedFilters.length > 0) {
      const start = searchParams.get('st_ts');
      const end = searchParams.get('end_ts');
      const filters: any[] = [];
      parsedFilters.forEach((f, i) => {
        const value = searchParams.get(f) as string;
        const isMeta = f.startsWith('meta_');
        const filterName = isMeta ? f.replace('meta_', '') : f;
        const searchPayload = isMeta
          ? { displayName: filterName }
          : { name: filterName };
        const filter = filterStore.findEvent(searchPayload);
        if (filter) {
          filter.value = [value];
        }
        filters.push(filter);
      });
      searchStore.edit({
        filters,
        startDate: start ? parseInt(start) : undefined,
        endDate: end ? parseInt(end) : undefined,
        rangeName: 'CUSTOM_RANGE',
        rangeValue: 'CUSTOM_RANGE',
      });
      searchStore.setUrlParsed();
      void searchStore.fetchSessions(true);
    } else {
      searchStore.setUrlParsed();
      void searchStore.fetchSessions(true);
      setTimeout(() => {
        initialFetchDoneRef.current = true;
      }, 500);
    }
  }, [filtersLoaded, searchStore.urlParsed, location.search, siteId]);

  const debouncedSearchCall = React.useMemo(
    () => debounceCall(() => searchStore.fetchSessions(true), 250),
    [searchStore],
  );
  React.useEffect(() => {
    if (!searchStore.urlParsed || !initialFetchDoneRef.current) return;
    debouncedSearchCall();
  }, [searchStore.instance.filters, searchStore.instance.eventsOrder]);

  React.useEffect(() => {
    const siteId = projectsStore.activeSiteId;
    searchStore.resetTags();
    if (siteId && lastFiltersSiteIdRef.current !== siteId) {
      lastFiltersSiteIdRef.current = siteId;
      filterStore
        .fetchFilters(siteId)
        .then(() => {
          setFiltersLoaded(true);
        })
        .catch((e) => {
          console.error(e);
          // if filters failed, there may be some sessions still available in
          // the list — unless the plain-load branch above already asked for them
          if (!searchStore.urlParsed) {
            searchStore.setUrlParsed();
            void searchStore.fetchSessions(true);
            setTimeout(() => {
              initialFetchDoneRef.current = true;
            }, 500);
          }
        });
    }
  }, [projectsStore.activeSiteId]);

  const fallbackTo = redirectToOnboarding
    ? withSiteId(ONBOARDING_REDIRECT_PATH, siteId)
    : withSiteId(routes.sessions(), siteId);

  return (
    <Suspense fallback={<Loader loading className="flex-1" />}>
      <StableRoutes>
        <Route
          path={`${CLIENT_PATH}/*`}
          element={<enhancedComponents.Client />}
        />
        <Route
          path={withSiteId(`${ONBOARDING_PATH}/*`, siteIdList)}
          element={<enhancedComponents.Onboarding />}
        />
        <Route
          path={SPOTS_LIST_PATH}
          element={<enhancedComponents.SpotsList />}
        />
        <Route path={SPOT_PATH} element={<enhancedComponents.Spot />} />

        <Route path="/integrations/*" element={<IntegrationsRedirect />} />
        <Route
          path={routes.mcpAuthorize()}
          element={<components.McpAuthorize />}
        />

        {/* DASHBOARD and Metrics */}
        <Route
          path={withSiteId(`${ALERTS_PATH}/*`, siteIdList)}
          element={<enhancedComponents.Dashboard />}
        />
        <Route
          path={withSiteId(`${ALERT_EDIT_PATH}/*`, siteIdList)}
          element={<enhancedComponents.Dashboard />}
        />
        <Route
          path={withSiteId(`${ALERT_CREATE_PATH}/*`, siteIdList)}
          element={<enhancedComponents.Dashboard />}
        />
        <Route
          path={withSiteId(METRICS_PATH, siteIdList)}
          element={<enhancedComponents.Dashboard />}
        />
        <Route
          path={withSiteId(`${METRICS_DETAILS}/*`, siteIdList)}
          element={<enhancedComponents.Dashboard />}
        />
        <Route
          path={withSiteId(`${METRICS_DETAILS_SUB}/*`, siteIdList)}
          element={<enhancedComponents.Dashboard />}
        />
        <Route
          path={withSiteId(`${DASHBOARD_PATH}/*`, siteIdList)}
          element={<enhancedComponents.Dashboard />}
        />
        <Route
          path={withSiteId(`${DASHBOARD_SELECT_PATH}/*`, siteIdList)}
          element={<enhancedComponents.Dashboard />}
        />
        <Route
          path={withSiteId(`${DASHBOARD_METRIC_CREATE_PATH}/*`, siteIdList)}
          element={<enhancedComponents.Dashboard />}
        />
        <Route
          path={withSiteId(`${DASHBOARD_METRIC_DETAILS_PATH}/*`, siteIdList)}
          element={<enhancedComponents.Dashboard />}
        />

        <Route
          path={withSiteId(MULTIVIEW_INDEX_PATH, siteIdList)}
          element={<enhancedComponents.Multiview />}
        />
        <Route
          path={withSiteId(MULTIVIEW_PATH, siteIdList)}
          element={<enhancedComponents.Multiview />}
        />
        <Route
          path={withSiteId(ASSIST_PATH, siteIdList)}
          element={<enhancedComponents.Assist />}
        />
        <Route
          path={withSiteId(RECORDINGS_PATH, siteIdList)}
          element={<enhancedComponents.Assist />}
        />
        <Route
          path={withSiteId(HIGHLIGHTS_PATH, siteIdList)}
          element={<enhancedComponents.Highlights />}
        />

        <Route
          path={withSiteId(`${SESSIONS_PATH}/*`, siteIdList)}
          element={<enhancedComponents.SessionsOverview />}
        />
        <Route
          path={withSiteId(NOTES_PATH, siteIdList)}
          element={<enhancedComponents.SessionsOverview />}
        />
        <Route
          path={withSiteId(`${BOOKMARKS_PATH}/*`, siteIdList)}
          element={<enhancedComponents.SessionsOverview />}
        />

        <Route
          path={withSiteId(SESSION_PATH, siteIdList)}
          element={<enhancedComponents.Session />}
        />
        <Route
          path={withSiteId(LIVE_SESSION_PATH, siteIdList)}
          element={<enhancedComponents.LiveSession />}
        />
        <Route
          path={withSiteId(routes.dataManagement.activity(), siteIdList)}
          element={<enhancedComponents.Activity />}
        />
        <Route
          path={withSiteId(routes.dataManagement.userPage(), siteIdList)}
          element={<enhancedComponents.UserPage />}
        />
        <Route
          path={withSiteId(routes.dataManagement.usersList(), siteIdList)}
          element={<enhancedComponents.UsersPage />}
        />
        <Route
          path={withSiteId(routes.dataManagement.eventsList(), siteIdList)}
          element={<enhancedComponents.EventsPage />}
        />
        <Route
          path={withSiteId(routes.dataManagement.properties(), siteIdList)}
          element={<enhancedComponents.PropertiesList />}
        />
        <Route
          path={withSiteId(routes.dataManagement.segmentPage(), siteIdList)}
          element={<enhancedComponents.SegmentPage />}
        />
        <Route
          path={withSiteId(routes.dataManagement.segments(), siteIdList)}
          element={<enhancedComponents.SegmentsPage />}
        />
        <Route
          path={withSiteId(routes.dataManagement.tags(), siteIdList)}
          element={<enhancedComponents.TagsPage />}
        />
        {Object.entries(routes.redirects).map(([fr, to]) => (
          <Route key={fr} path={fr} element={<Navigate to={to} replace />} />
        ))}
        {SAAS_ROUTES.filter((route) => route.enabled()).map(
          ({ path, Component }) => (
            <Route
              key={path}
              path={withSiteId(path, siteIdList)}
              element={<Component />}
            />
          ),
        )}
        <Route path="*" element={<Navigate to={fallbackTo} replace />} />
      </StableRoutes>
    </Suspense>
  );
}

export default observer(PrivateRoutes);
